/**
 * ATO Audit Risk - Client Detail Page
 *
 * Opens from the audit-risk table. Explains how the risk score was calculated,
 * the weighting of each factor, specific mitigation steps for each detected
 * risk, and authoritative ATO resources.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ShieldAlert, Shield, ShieldCheck, ArrowLeft, AlertTriangle, CheckCircle2, ExternalLink,
  BookOpen, Info, Target, Zap, FileText, Link2, Users, BarChart3, Calculator,
  ClipboardList, Scale,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' },
  itemStyle: { color: '#94a3b8' },
  wrapperStyle: { outline: 'none' as const },
  cursor: false,
};

const RISK_COLORS: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#22c55e',
};

// ==================== Risk factor metadata ====================
// Maps risk strings returned by the API to structured metadata (weighting, mitigation, citations).
interface RiskFactorMeta {
  label: string;
  weight: number;
  icon: any;
  why: string;
  mitigations: string[];
  internalTools?: { title: string; href: string }[];
}

/**
 * Match a risk string from the API to its metadata. Order matters - most
 * specific first, fallback last.
 */
function classifyRisk(risk: string): { key: string; meta: RiskFactorMeta } {
  const r = risk.toLowerCase();
  if (r.includes('no industry')) {
    return { key: 'noIndustry', meta: {
      label: 'Industry not set',
      weight: 15,
      icon: Info,
      why: 'Without an industry, we can\'t benchmark the client\'s ratios against the ATO Small Business Benchmarks. Deviations from benchmarks are the single biggest ATO audit selection signal.',
      mitigations: [
        'Open the client\'s profile and set the correct ANZSIC-aligned industry from the dropdown.',
        'Match as closely as possible to the benchmark categories published by the ATO (retail → specific retail segment, not just "retail").',
        'Re-run this risk scan - the benchmark comparison will then run automatically.',
      ],
    } };
  }
  if (r.includes('not connected to xero') || r.includes('xero')) {
    return { key: 'noXero', meta: {
      label: 'Not connected to accounting software',
      weight: 10,
      icon: Link2,
      why: 'Without live accounting data we can\'t verify income and expense ratios against benchmarks. It also means bookkeeping is likely inconsistent - a known ATO audit flag.',
      mitigations: [
        'Go to Integrations → Xero and connect the client\'s organisation via OAuth.',
        'Confirm the correct Xero org is mapped to this client (check ABN matches).',
        'Allow 2-5 minutes for the first data pull, then re-run the risk scan.',
      ],
      internalTools: [
        { title: 'Xero Integration', href: '/panel/integrations/xero' },
      ],
    } };
  }
  if (r.includes('no abn')) {
    return { key: 'noAbn', meta: {
      label: 'No ABN on file',
      weight: 10,
      icon: FileText,
      why: 'An ABN is mandatory for GST-registered businesses, BAS lodgements, and most business-to-business transactions. A missing ABN on the file is an administrative red flag and blocks automated compliance checks.',
      mitigations: [
        'Look up the client on ABN Lookup (abr.business.gov.au) by entity name.',
        'Update the ABN on the client profile.',
        'If the business does not have an ABN, advise them to apply via ABR - some trust/SMSF structures require it.',
      ],
    } };
  }
  if (r.includes('no documents')) {
    return { key: 'noDocs', meta: {
      label: 'No documents uploaded',
      weight: 10,
      icon: FileText,
      why: 'The ATO expects businesses to retain substantiation records for 5 years. An empty document library means audit queries cannot be answered quickly, and substantiation weaknesses may trigger denied deductions.',
      mitigations: [
        'Request prior-year tax returns, BAS lodgements and workpapers from the client.',
        'Upload invoices, receipts, bank statements, contracts to the client\'s knowledge base.',
        'Set up automatic sync from Google Drive, OneDrive or Dropbox if the client uses one of those.',
        'Enable Xero document attachments - attach every invoice\'s source document in Xero.',
      ],
    } };
  }
  if (r.includes('unusually low')) {
    return { key: 'expLow', meta: {
      label: 'Expenses unusually LOW vs benchmark',
      weight: 25,
      icon: AlertTriangle,
      why: 'Expenses significantly below the ATO industry benchmark is one of the strongest audit triggers - the ATO suspects unreported cash income (expenses are low because income was skimmed). The ATO explicitly lists this pattern as high-risk.',
      mitigations: [
        'Verify there are no unreported sales - reconcile bank deposits, Square/Stripe takings, and cash income to the income ledger.',
        'Check the expense categorisation in Xero - genuine expenses may be miscoded to wash accounts or the director\'s loan.',
        'Prepare a written explanation for any legitimate reason expenses are lower (e.g., new owner, premises closure, pandemic lockdown, pivot to online).',
        'Document the reason in the working papers - if the ATO audits, contemporaneous explanation carries much more weight than post-hoc.',
        'Consider a voluntary disclosure if under-reporting is found - penalties are significantly reduced for voluntary disclosures (up to 80% reduction).',
      ],
      internalTools: [
        { title: 'Reconciliation - TB vs P&L vs BS', href: '/panel/intelligence/reconciliation' },
        { title: 'Compliance Checker', href: '/panel/compliance/checker' },
      ],
    } };
  }
  if (r.includes('unusually high')) {
    return { key: 'expHigh', meta: {
      label: 'Expenses unusually HIGH vs benchmark',
      weight: 20,
      icon: AlertTriangle,
      why: 'Expenses significantly above the ATO benchmark suggests inflated deductions - personal expenses routed through the business, overstated depreciation, or fabricated invoices. This pattern is aggressively audited.',
      mitigations: [
        'Review the largest expense categories - identify any personal expenses incorrectly claimed (home internet, mobile, meals, car, travel).',
        'Verify substantiation exists for all material claims: invoices, FBT-reportable entertainment, motor vehicle logbooks, travel diaries.',
        'Check related-party expenses - directors\' fees, management charges, rent to associated entities should be at arm\'s length and commercially justifiable.',
        'Investigate any round-number or repetitive amounts - classic fabricated-invoice patterns.',
        'Engage the client to produce supporting records now (before an audit) - missing documents found under audit pressure carry far less credibility.',
      ],
      internalTools: [
        { title: 'Compliance Checker', href: '/panel/compliance/checker' },
        { title: 'Missing Documents', href: '/panel/compliance/missing-documents' },
      ],
    } };
  }
  if (r.includes('outside benchmark')) {
    return { key: 'expOutside', meta: {
      label: 'Expenses outside benchmark range',
      weight: 10,
      icon: BarChart3,
      why: 'A moderate deviation from the benchmark band. Not in itself an audit trigger but may combine with other factors. The ATO uses benchmark deviation as one signal among many in its Risk Differentiation Framework.',
      mitigations: [
        'Open the Compliance Checker to see the full benchmark comparison.',
        'Identify the specific expense categories driving the deviation.',
        'Document any legitimate business reason - new business phase, unusual one-off expenses, industry sub-segment that differs from the generic benchmark.',
      ],
      internalTools: [
        { title: 'Compliance Checker', href: '/panel/compliance/checker' },
      ],
    } };
  }
  // Generic fallback
  return { key: 'generic', meta: {
    label: risk,
    weight: 0,
    icon: AlertTriangle,
    why: 'This flag indicates an administrative or data issue affecting the audit risk assessment.',
    mitigations: [
      'Review the flag detail and address the underlying data quality issue.',
      'Re-run the risk scan once resolved.',
    ],
  } };
}

// ==================== Scoring methodology reference ====================
// All 7 factors with their weights, used to show the full scoring table.
const SCORING_FACTORS = [
  { name: 'No industry set', weight: 15, note: 'Prevents benchmark comparison' },
  { name: 'Not connected to Xero', weight: 10, note: 'No live financial data' },
  { name: 'No ABN on file', weight: 10, note: 'Administrative / compliance flag' },
  { name: 'No documents uploaded', weight: 10, note: 'Substantiation weakness' },
  { name: 'Expenses unusually LOW (>10% below benchmark low)', weight: 25, note: 'Possible unreported income' },
  { name: 'Expenses unusually HIGH (>10% above benchmark high)', weight: 20, note: 'Possible inflated deductions' },
  { name: 'Expenses outside benchmark range (small deviation)', weight: 10, note: 'Single-factor deviation' },
];

// ==================== ATO audit references ====================
interface AtoRef { title: string; source: string; url: string; description: string; icon: any; }

const ATO_REFERENCES: AtoRef[] = [
  {
    title: 'Small Business Benchmarks',
    source: 'ATO',
    url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/small-business-benchmarks',
    description: 'Industry ratios for gross profit, labour, rent, and motor vehicle as a proportion of turnover. Businesses outside these ranges are more likely to be selected for review.',
    icon: BarChart3,
  },
  {
    title: 'How we use benchmarks',
    source: 'ATO',
    url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/small-business-benchmarks/how-we-use-benchmarks',
    description: 'The ATO uses industry benchmarks as one input to its Risk Differentiation Framework. Significant deviation triggers data-matching and risk reviews.',
    icon: Target,
  },
  {
    title: 'What to do if we contact you about an audit',
    source: 'ATO',
    url: 'https://www.ato.gov.au/about-ato/managing-the-tax-and-super-system/in-detail/building-confidence/reviews-and-audits',
    description: 'The ATO\'s review and audit process. Includes information about pre-audit inquiries, the Taxpayers\' Charter, response timeframes, and dispute options.',
    icon: ClipboardList,
  },
  {
    title: 'Record-keeping for business',
    source: 'ATO',
    url: 'https://www.ato.gov.au/businesses-and-organisations/preparing-lodging-and-paying/record-keeping-for-business',
    description: 'Core record-keeping requirements - keep records for 5 years, in English, explain all transactions. Poor records dramatically increase audit exposure.',
    icon: FileText,
  },
  {
    title: 'Voluntary disclosure - reducing penalties',
    source: 'ATO',
    url: 'https://www.ato.gov.au/about-ato/managing-the-tax-and-super-system/in-detail/building-confidence/tax-crime/voluntary-disclosures',
    description: 'Coming forward before the ATO starts an audit can reduce penalties by up to 80%. Critical strategy when under-reporting or misclassification is discovered internally.',
    icon: Shield,
  },
  {
    title: 'Private rulings',
    source: 'ATO',
    url: 'https://www.ato.gov.au/about-ato/managing-the-tax-and-super-system/administration-of-the-tax-and-super-system/private-rulings',
    description: 'Binding written advice from the ATO on how a tax law applies to a specific arrangement. Useful for complex or high-risk positions - provides certainty and reduces audit risk for that specific position.',
    icon: Scale,
  },
  {
    title: 'ABN Lookup',
    source: 'ABR',
    url: 'https://abr.business.gov.au/',
    description: 'Official Australian Business Register lookup. Use to verify ABN, GST registration status, entity name and structure.',
    icon: FileText,
  },
];

// ==================== Component ====================

export default function AuditRiskDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const preloaded = (location.state as any)?.client;

  const { data, isLoading } = useQuery({
    queryKey: ['audit-risk'],
    queryFn: async () => { const r = await apiClient.get('/compliance/audit-risk'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const client = data?.clients?.find((c: any) => c.clientId === clientId) || preloaded;

  if (isLoading && !preloaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-3 text-sm text-muted-foreground">Loading audit risk assessment...</span>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/compliance/audit-risk')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Audit Risk
        </Button>
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
            <p>Client not found in the current risk scan.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const riskLevel = client.riskLevel || 'low';
  const riskColor = RISK_COLORS[riskLevel];
  const risks = (client.risks || []) as string[];

  // Enrich each risk with structured metadata
  const enrichedRisks = risks.map(r => ({ original: r, ...classifyRisk(r) }));

  // Chart: contribution of each factor to the risk score
  const chartData = enrichedRisks.map((r, i) => ({
    name: r.meta.label.length > 30 ? r.meta.label.substring(0, 28) + '...' : r.meta.label,
    weight: r.meta.weight,
    fill: r.meta.weight >= 20 ? '#ef4444' : r.meta.weight >= 15 ? '#f97316' : r.meta.weight >= 10 ? '#f59e0b' : '#94a3b8',
    _i: i,
  })).filter(d => d.weight > 0);

  const totalExplainedScore = enrichedRisks.reduce((s, r) => s + r.meta.weight, 0);
  const cappedScore = Math.min(100, totalExplainedScore);

  return (
    <div className="space-y-6">
      {/* Back nav */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/panel/compliance/audit-risk')} className="gap-2">
        <ArrowLeft className="h-4 w-4" /> Back to Audit Risk
      </Button>

      <PageHeader
        icon={ShieldAlert}
        title={`${client.clientName} - Audit Risk Detail`}
        description="How the risk score is calculated, per-factor mitigations, and ATO citations"
        iconColor={riskLevel === 'high' ? 'bg-red-500/10 text-red-500' : riskLevel === 'medium' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-green-500/10 text-green-500'}
      />

      {/* Risk Score Banner */}
      <Card className="border-l-4" style={{ borderLeftColor: riskColor }}>
        <CardContent className="pt-5 pb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: riskColor }}>
                {client.riskScore}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h2 className="text-lg font-semibold">{client.clientName}</h2>
                  {client.industry && <Badge variant="outline" className="text-[10px]">{client.industry}</Badge>}
                  <Badge style={{ backgroundColor: riskColor + '20', color: riskColor, border: 'none' }} className="text-[10px] uppercase font-semibold">
                    {riskLevel} risk
                  </Badge>
                  {client.xeroConnected
                    ? <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">Accounting connected</Badge>
                    : <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">No accounting</Badge>}
                  {client.abn
                    ? <Badge variant="outline" className="text-[10px]">ABN on file</Badge>
                    : <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">No ABN</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">
                  Risk score <span className="font-semibold">{client.riskScore}/100</span> · {risks.length} detected risk factor{risks.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {riskLevel === 'high' && <ShieldAlert className="h-8 w-8 text-red-500" />}
              {riskLevel === 'medium' && <Shield className="h-8 w-8 text-yellow-500" />}
              {riskLevel === 'low' && <ShieldCheck className="h-8 w-8 text-green-500" />}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How is the score calculated? */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />
            How is this risk score calculated?
          </CardTitle>
          <CardDescription className="text-xs">
            Additive model based on data quality flags and ATO benchmark comparison. Each triggered factor adds points; the total is capped at 100.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-md bg-muted/40 font-mono text-xs leading-relaxed">
            <div><span className="text-muted-foreground">Step 1:</span> Start with 0 points.</div>
            <div><span className="text-muted-foreground">Step 2:</span> Add points for every risk factor that applies (see table below).</div>
            <div><span className="text-muted-foreground">Step 3:</span> Cap final total at 100.</div>
            <div className="mt-2"><span className="text-muted-foreground">Risk bands:</span> High ≥ 60 · Medium 30-59 · Low &lt; 30</div>
          </div>

          {/* Scoring factors table */}
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Risk factor</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Weight</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Rationale</th>
                  <th className="px-3 py-2 text-center font-medium text-muted-foreground">This client</th>
                </tr>
              </thead>
              <tbody>
                {SCORING_FACTORS.map((f, i) => {
                  // Does this factor appear in client.risks? match by keyword
                  const triggered = risks.some(r => {
                    const rl = r.toLowerCase();
                    if (f.name.includes('No industry')) return rl.includes('no industry');
                    if (f.name.includes('Xero')) return rl.includes('xero') && rl.includes('not');
                    if (f.name.includes('No ABN')) return rl.includes('no abn');
                    if (f.name.includes('No documents')) return rl.includes('no documents');
                    if (f.name.includes('LOW')) return rl.includes('unusually low');
                    if (f.name.includes('HIGH')) return rl.includes('unusually high');
                    if (f.name.includes('outside benchmark')) return rl.includes('outside benchmark');
                    return false;
                  });
                  return (
                    <tr key={i} className={cn('border-b last:border-0', triggered && 'bg-red-500/5')}>
                      <td className="px-3 py-2">{f.name}</td>
                      <td className="px-3 py-2 text-right font-mono">+{f.weight}</td>
                      <td className="px-3 py-2 text-muted-foreground">{f.note}</td>
                      <td className="px-3 py-2 text-center">
                        {triggered
                          ? <span className="text-red-600 font-semibold">✓ Triggered</span>
                          : <span className="text-muted-foreground">-</span>}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-muted/30 font-semibold">
                  <td className="px-3 py-2">Subtotal (before cap)</td>
                  <td className="px-3 py-2 text-right font-mono">{totalExplainedScore}</td>
                  <td className="px-3 py-2 text-muted-foreground">Max 100</td>
                  <td className="px-3 py-2 text-center font-mono">{cappedScore}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Contribution chart */}
          {chartData.length > 0 && (
            <div>
              <p className="text-xs font-semibold mb-2">Factor contribution to this client&apos;s score</p>
              <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 36)}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis type="number" domain={[0, 30]} tick={{ fontSize: 10, fill: '#888' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#999' }} width={160} />
                  <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => `${v} pts`} />
                  <Bar dataKey="weight" name="Points added" radius={[0, 4, 4, 0]} activeBar={false}>
                    {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-risk analysis + mitigation */}
      {enrichedRisks.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="h-4 w-4" />
              Detected Risk Factors & Mitigation
            </CardTitle>
            <CardDescription className="text-xs">
              Specific actions to reduce the risk score. Sorted by highest weight first.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {enrichedRisks
              .slice()
              .sort((a, b) => b.meta.weight - a.meta.weight)
              .map((r, i) => {
                const Icon = r.meta.icon;
                const weight = r.meta.weight;
                const severity = weight >= 20 ? 'critical' : weight >= 15 ? 'high' : weight >= 10 ? 'medium' : 'low';
                const style = severity === 'critical' ? { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-600' }
                  : severity === 'high' ? { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-600' }
                  : severity === 'medium' ? { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-600' }
                  : { bg: 'bg-muted/40', border: 'border-muted', text: 'text-muted-foreground' };

                return (
                  <div key={i} className={cn('p-4 rounded-md border', style.bg, style.border)}>
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="flex items-start gap-3">
                        <Icon className={cn('h-5 w-5 mt-0.5', style.text)} />
                        <div>
                          <p className={cn('text-sm font-semibold', style.text)}>{r.meta.label}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{r.original}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn('text-[9px]', style.text)}>
                        +{weight} pts
                      </Badge>
                    </div>

                    <div className="space-y-2 mt-3">
                      <div className="p-2 rounded bg-background/60">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-0.5">Why this matters</p>
                        <p className="text-xs leading-relaxed">{r.meta.why}</p>
                      </div>

                      <div className="p-2 rounded bg-background/60">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">How to mitigate</p>
                        <ul className="text-xs space-y-1 leading-relaxed">
                          {r.meta.mitigations.map((m, j) => (
                            <li key={j} className="flex items-start gap-2">
                              <CheckCircle2 className="h-3 w-3 text-green-600 mt-0.5 shrink-0" />
                              <span>{m}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {r.meta.internalTools && r.meta.internalTools.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {r.meta.internalTools.map(t => (
                            <button
                              key={t.href}
                              onClick={() => navigate(t.href)}
                              className="px-3 py-1 text-[11px] rounded-md border bg-background hover:bg-muted/60 transition-colors flex items-center gap-1"
                            >
                              <Zap className="h-3 w-3" />
                              {t.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>
      )}

      {enrichedRisks.length === 0 && (
        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-green-500 opacity-60" />
            <p className="text-sm font-semibold">No risk factors detected</p>
            <p className="text-xs text-muted-foreground mt-1">This client is clean on all assessed factors. Continue monitoring quarterly.</p>
          </CardContent>
        </Card>
      )}

      {/* Recommended internal tools (always shown for context) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Recommended RoniDoc tools
          </CardTitle>
          <CardDescription className="text-xs">Run these to dig deeper and document your findings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <button onClick={() => navigate('/panel/compliance/checker')} className="flex items-start gap-3 p-3 rounded-md border hover:bg-muted/40 transition-colors text-left">
              <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><Shield className="h-4 w-4" /></div>
              <div><p className="text-sm font-medium">Compliance Checker</p><p className="text-[11px] text-muted-foreground">Full benchmark comparison for this client</p></div>
            </button>
            <button onClick={() => navigate('/panel/intelligence/reconciliation')} className="flex items-start gap-3 p-3 rounded-md border hover:bg-muted/40 transition-colors text-left">
              <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><Scale className="h-4 w-4" /></div>
              <div><p className="text-sm font-medium">Reconciliation</p><p className="text-[11px] text-muted-foreground">Trial Balance vs P&L vs Balance Sheet integrity check</p></div>
            </button>
            <button onClick={() => navigate('/panel/compliance/missing-documents')} className="flex items-start gap-3 p-3 rounded-md border hover:bg-muted/40 transition-colors text-left">
              <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
              <div><p className="text-sm font-medium">Missing Documents</p><p className="text-[11px] text-muted-foreground">Substantiation gap analysis</p></div>
            </button>
            <button onClick={() => navigate('/panel/compliance/bas')} className="flex items-start gap-3 p-3 rounded-md border hover:bg-muted/40 transition-colors text-left">
              <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><Calculator className="h-4 w-4" /></div>
              <div><p className="text-sm font-medium">BAS Validator</p><p className="text-[11px] text-muted-foreground">Check current BAS period against accounting data</p></div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* ATO / Regulatory References */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            ATO References & Citations
          </CardTitle>
          <CardDescription className="text-xs">Official ATO sources on benchmarks, audit process, record-keeping, and voluntary disclosures</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {ATO_REFERENCES.map((ref, i) => {
              const Icon = ref.icon;
              return (
                <a
                  key={i}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-3 p-3 rounded-md border hover:bg-muted/40 transition-colors"
                >
                  <div className="h-8 w-8 rounded-md bg-muted text-foreground flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold">{ref.title}</span>
                      <Badge variant="outline" className="text-[9px]">{ref.source}</Badge>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{ref.description}</p>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="mt-4 p-3 rounded-md bg-amber-500/5 border border-amber-500/20 text-[11px] text-muted-foreground leading-relaxed">
            <strong className="text-amber-600">Important:</strong> The risk score is decision-support only and does not represent an actual ATO-issued risk rating. Always exercise professional judgment. For positions with material audit exposure, consider obtaining a private ruling from the ATO for binding certainty.
          </div>
        </CardContent>
      </Card>

      {/* Quick link to client file */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => navigate(`/panel/files/clients/${client.clientId}`)} className="gap-2">
          <Users className="h-4 w-4" /> Open client file
        </Button>
      </div>
    </div>
  );
}
